import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getBrokers, joinWaitlist, type Broker, type BrokersResponse } from '../api/brokerApi'

export default function BrokerMarketplace() {
  const navigate = useNavigate()
  const [data, setData] = useState<BrokersResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [waitlistEmail, setWaitlistEmail] = useState('')
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false)

  useEffect(() => {
    getBrokers()
      .then((res) => {
        // Auto-route: if only 1 broker, go directly to their profile
        if (res.auto_route && res.brokers.length === 1) {
          navigate(`/brokers/${res.brokers[0].id}`, { replace: true })
          return
        }
        setData(res)
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))
  }, [navigate])

  const handleWaitlist = async () => {
    if (!waitlistEmail) return
    try {
      await joinWaitlist({ email: waitlistEmail })
      setWaitlistSubmitted(true)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to join waitlist')
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading brokers...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-red-500">{error}</div>
      </div>
    )
  }

  // No brokers available — show waitlist
  if (data?.waitlist) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Brokers Currently at Capacity</h2>
          <p className="text-gray-600 mb-6">
            Join the waitlist and we'll notify you when a spot opens.
          </p>
          {waitlistSubmitted ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-700">
              You've been added to the waitlist. We'll be in touch!
            </div>
          ) : (
            <div className="flex gap-2">
              <input
                type="email"
                placeholder="Your email address"
                value={waitlistEmail}
                onChange={(e) => setWaitlistEmail(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                onClick={handleWaitlist}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Notify Me
              </button>
            </div>
          )}
          <button
            onClick={() => navigate('/')}
            className="mt-4 text-blue-600 hover:underline text-sm"
          >
            Back to Home
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Find a Mortgage Broker</h1>
          <p className="text-gray-600 mt-2">
            Connect with a licensed mortgage broker who can help you find the right loan.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {data?.brokers.map((broker) => (
            <BrokerCard
              key={broker.id}
              broker={broker}
              onClick={() => navigate(`/brokers/${broker.id}`)}
            />
          ))}
        </div>

        {data?.brokers.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No brokers are currently accepting new clients.
          </div>
        )}

        <div className="mt-8 text-center flex justify-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="text-blue-600 hover:underline"
          >
            Back to Home
          </button>
          <button
            onClick={() => navigate('/customer')}
            className="text-gray-500 hover:underline"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  )
}

function BrokerCard({ broker, onClick }: { broker: Broker; onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow cursor-pointer p-6"
    >
      <div className="flex items-start gap-4">
        <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xl font-bold flex-shrink-0">
          {broker.business_name?.[0] || broker.email[0].toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 truncate">
            {broker.business_name || 'Mortgage Broker'}
          </h3>
          {broker.years_experience && (
            <p className="text-sm text-gray-500">{broker.years_experience} years experience</p>
          )}
          {broker.specialisation && (
            <p className="text-sm text-blue-600 mt-1">{broker.specialisation}</p>
          )}
        </div>
      </div>
      {broker.bio && (
        <p className="text-gray-600 text-sm mt-4 line-clamp-3">{broker.bio}</p>
      )}
      <div className="mt-4 flex items-center justify-between">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Accepting clients
        </span>
        <span className="text-blue-600 text-sm font-medium">View Profile →</span>
      </div>
    </div>
  )
}
